import { useState, useRef, useEffect, useCallback, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Mic, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { composeTranscript, classifySpeechError } from '@/lib/speech-utils';

/**
 * Dictado por voz (Web Speech API del navegador, sin subir audio).
 *
 * Mientras graba muestra una barra estilo WhatsApp: punto rojo + timer +
 * transcript EN VIVO (interim results — la prueba visible de que está
 * escuchando) + ondas animadas + cancelar/listo. Nunca usa el ícono MicOff
 * para "grabando" (ese ícono significa mute — era la causa de que no se
 * entendiera si estaba tomando la voz).
 *
 * ## Keep-alive (14/09/2026)
 *
 * Chrome reconoce en la nube y cierra la sesión solo: por silencio (~60 s),
 * por un `network` a mitad del dictado, o por su tope de audio continuo. La
 * versión anterior trataba el `network` como cancelación y `onend` BORRABA
 * todo lo acumulado — una clapper dictó largo y perdió el texto entero. Y
 * cuando Chrome cortaba por silencio, la barra desaparecía y la persona seguía
 * hablando al aire.
 *
 * Ahora la grabación dura hasta que la persona aprieta ✓ o ✕:
 * - Cada sesión de Chrome es una INSTANCIA NUEVA (patrón de liveSession.ts):
 *   no hay `InvalidStateError` por reusar, y `lang` se lee al arrancar.
 * - `onend` nunca descarta: consolida el interim en los finales y decide
 *   reiniciar (si la persona sigue queriendo grabar) o entregar.
 * - Los reinicios NO tocan `phase`, el timer ni `onRecordingChange`: la barra
 *   y el mm:ss siguen, y el Textarea del parent no parpadea.
 * - Cancelar/desmontar desenganchan los handlers ANTES de `abort()`: la
 *   limpieza es determinista, no depende de si el navegador dispara `onend`.
 * - Frenos: N sesiones seguidas sin oír nada, o el tope total de minutos ⇒ se
 *   entrega lo que haya y se cierra con un aviso.
 *
 * Limitación inherente: entre el `onend` de una sesión y el `onstart` de la
 * siguiente (~0,3-1 s) no se captura audio. Solo se resolvería grabando con
 * MediaRecorder y transcribiendo del lado del servidor.
 */
interface VoiceInputProps {
  onTranscript: (text: string) => void;
  className?: string;
  /** 'panel' (default): botón + transcript editable + "Usar transcripción".
   *  'inline': mic en la fila del input; al grabar, la barra reemplaza al
   *  input del parent (avisado vía onRecordingChange) y al terminar el texto
   *  se entrega por onTranscript. */
  variant?: 'panel' | 'inline';
  /** BCP-47 del reconocimiento (es-AR / pt-BR / en-US). D-Talk lo pasa según
   *  el idioma elegido de la conversación. */
  lang?: string;
  /** El parent oculta su Textarea+enviar mientras esto es true. */
  onRecordingChange?: (recording: boolean) => void;
}

// Browser SpeechRecognition API types
declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

type Phase = 'idle' | 'recording' | 'denied';

/** Aviso al cerrar (toast) — lo setean los errores y los topes; lo consume deliverAndIdle. */
type EndNotice = 'denied' | 'no_mic' | 'cut_off' | 'silence' | 'max_duration';
type Timer = ReturnType<typeof setTimeout>;

/** Sesiones seguidas sin oír nada antes de cerrar solos (~30-35 s de silencio). */
const MAX_EMPTY_SESSIONS = 3;
/** Tope duro de un dictado: cierra un mic olvidado abierto; entrega todo. */
const MAX_TOTAL_MS = 10 * 60_000;
/** `start()` que tira seguidos antes de rendirse. */
const MAX_START_ATTEMPTS = 3;
/** Base del backoff entre sesiones (×2 por sesión vacía o intento fallido). */
const RESTART_DELAY_MS = 200;
/** Tras `stop()`: si `onend` no llega, entregar igual para no dejar la barra colgada. */
const STOP_FALLBACK_MS = 2500;
/** Cuánto dura la línea "se cortó la conexión, seguimos grabando…". */
const LIVE_NOTICE_MS = 4000;

function clearTimer(ref: MutableRefObject<Timer | null>) {
  if (ref.current) {
    clearTimeout(ref.current);
    ref.current = null;
  }
}

/** Sin handlers, una instancia vieja no puede reiniciar, entregar ni tocar estado. */
function detach(rec: SpeechRecognition | null) {
  if (!rec) return;
  rec.onstart = null;
  rec.onresult = null;
  rec.onerror = null;
  rec.onend = null;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Ondas animadas (CSS puro, escalonadas). No abrimos una segunda captura del
 *  mic solo para nivel real: el transcript en vivo ya muestra que escucha. */
function Waveform() {
  const heights = [10, 16, 22, 14, 20, 12];
  return (
    <div className="flex items-center gap-[3px] shrink-0" aria-hidden>
      {heights.map((h, i) => (
        <span
          key={i}
          className="w-[3px] rounded-full bg-red-500/80 animate-pulse"
          style={{ height: `${h}px`, animationDelay: `${i * 120}ms`, animationDuration: '900ms' }}
        />
      ))}
    </div>
  );
}

interface RecordingBarProps {
  elapsed: number;
  transcript: string;
  placeholder: string;
  cancelLabel: string;
  doneLabel: string;
  onCancel: () => void;
  onDone: () => void;
}

function RecordingBar({ elapsed, transcript, placeholder, cancelLabel, doneLabel, onCancel, onDone }: RecordingBarProps) {
  // El transcript en vivo se lee entero, no en un renglón recortado (pedido de
  // Sol 14/09: "que puedan previsualizar lo que dijeron fácil"). Crece hasta
  // ~3 líneas y después scrollea, siempre pegado al final: lo último dicho es
  // lo que importa mientras dictás, pero lo anterior tiene que poder mirarse.
  const transcriptRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript]);

  return (
    <div className="flex flex-1 min-w-0 items-center gap-3 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50/60 dark:bg-red-950/20 px-3 py-2">
      <span className="relative flex h-3 w-3 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
      </span>
      <span className="text-sm font-medium tabular-nums text-red-600 dark:text-red-400 shrink-0">
        {formatElapsed(elapsed)}
      </span>
      <Waveform />
      <p
        ref={transcriptRef}
        className={cn(
          'flex-1 min-w-0 max-h-15 overflow-y-auto whitespace-pre-wrap wrap-break-word text-sm leading-5',
          transcript ? 'text-foreground' : 'text-muted-foreground italic'
        )}
      >
        {transcript || placeholder}
      </p>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onCancel}
        className="h-8 w-8 rounded-full shrink-0 text-muted-foreground hover:text-red-600"
        title={cancelLabel}
      >
        <X className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        onClick={onDone}
        className="h-8 w-8 rounded-full shrink-0 bg-teal-600 hover:bg-teal-700 text-white"
        title={doneLabel}
      >
        <Check className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function VoiceInput({
  onTranscript,
  className,
  variant = 'panel',
  lang = 'es-AR',
  onRecordingChange,
}: VoiceInputProps) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);
  const [hasTranscript, setHasTranscript] = useState(false); // variante panel: transcript editable entregado
  const [elapsed, setElapsed] = useState(0);
  // Línea chica arriba de la barra mientras Chrome reconecta; se va sola.
  const [reconnecting, setReconnecting] = useState(false);

  /** Sesión de Chrome ABIERTA. null = no hay sesión (se anula en onend). */
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  /** Intención de la persona: true desde ▶ hasta ✓ / ✕ / tope / error fatal. onend reinicia solo si es true. */
  const wantsRecordingRef = useRef(false);
  /** Finales acumulados de TODAS las sesiones del dictado, ya normalizados. */
  const finalsRef = useRef('');
  /** Interim de la sesión abierta; en onend se consolida en finalsRef, nunca se tira. */
  const interimRef = useRef('');
  /** La sesión abierta produjo algún result con texto. */
  const sessionHadSpeechRef = useRef(false);
  /** Sesiones seguidas sin ningún result: el freno del loop. */
  const emptySessionsRef = useRef(0);
  /** start() fallidos seguidos dentro de un reinicio. */
  const startAttemptsRef = useRef(0);
  /** Qué aviso mostrar al entregar. onstart limpia 'cut_off' (la red volvió). */
  const pendingNoticeRef = useRef<EndNotice | null>(null);
  const restartTimerRef = useRef<Timer | null>(null);
  const stopFallbackTimerRef = useRef<Timer | null>(null);
  const maxDurationTimerRef = useRef<Timer | null>(null);
  const liveNoticeTimerRef = useRef<Timer | null>(null);

  // Los handlers viven más que un render: todo lo que leen va por ref.
  const langRef = useRef(lang);
  langRef.current = lang;
  const tRef = useRef(t);
  tRef.current = t;
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const onRecordingChangeRef = useRef(onRecordingChange);
  onRecordingChangeRef.current = onRecordingChange;
  const variantRef = useRef(variant);
  variantRef.current = variant;

  const clearAllTimers = () => {
    clearTimer(restartTimerRef);
    clearTimer(stopFallbackTimerRef);
    clearTimer(maxDurationTimerRef);
    clearTimer(liveNoticeTimerRef);
  };

  const showReconnecting = () => {
    setReconnecting(true);
    clearTimer(liveNoticeTimerRef);
    liveNoticeTimerRef.current = setTimeout(() => setReconnecting(false), LIVE_NOTICE_MS);
  };

  const showEndNotice = (kind: Exclude<EndNotice, 'denied'>, hadText: boolean) => {
    const k = (s: string) => tRef.current(`performance.voice_input.${s}`);
    const kept = hadText ? { description: k('kept_partial') } : undefined;
    switch (kind) {
      case 'cut_off':
        if (hadText) toast.message(k('cut_off'), kept);
        else toast.message(k('error'));
        break;
      case 'no_mic':
        toast.message(k('no_mic'), kept);
        break;
      case 'silence':
        if (hadText) toast.message(k('silence_stop'), kept);
        else toast.message(k('nothing_heard'));
        break;
      case 'max_duration':
        toast.message(tRef.current('performance.voice_input.max_duration', { minutes: MAX_TOTAL_MS / 60_000 }));
        break;
    }
  };

  /**
   * Única salida de 'recording' que entrega: ✓ explícito, tope, error fatal o
   * freno de sesiones vacías. Política "lo que haya": lo dictado siempre llega.
   */
  const deliverAndIdle = () => {
    clearAllTimers();
    wantsRecordingRef.current = false;
    const rec = recognitionRef.current; // solo no-null en el fallback de stop()
    if (rec) {
      detach(rec);
      recognitionRef.current = null;
      try { rec.abort(); } catch { /* ya cerrada */ }
    }

    const text = composeTranscript(finalsRef.current, interimRef.current);
    finalsRef.current = '';
    interimRef.current = '';
    const notice = pendingNoticeRef.current;
    pendingNoticeRef.current = null;
    setReconnecting(false);

    if (text) {
      if (variantRef.current === 'inline') {
        onTranscriptRef.current(text);
        setTranscript('');
      } else {
        setTranscript(text);
        setHasTranscript(true);
      }
    } else {
      setTranscript('');
    }
    setPhase(notice === 'denied' ? 'denied' : 'idle');
    onRecordingChangeRef.current?.(false);
    if (notice && notice !== 'denied') showEndNotice(notice, Boolean(text));
  };

  /** Abre UNA sesión de Chrome (instancia nueva). false si no pudo arrancar. */
  const startSession = (): boolean => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) return false;
    detach(recognitionRef.current); // defensivo: nunca dos sesiones vivas

    const rec = new SpeechRecognitionAPI();
    rec.lang = langRef.current; // leído acá: cambiar `lang` mid-dictado ya no aborta
    rec.continuous = true;
    rec.interimResults = true;

    rec.onstart = () => {
      if (recognitionRef.current !== rec) return;
      // La red volvió: el aviso en vivo se va y el 'cut_off' pendiente no aplica.
      setReconnecting(false);
      clearTimer(liveNoticeTimerRef);
      if (pendingNoticeRef.current === 'cut_off') pendingNoticeRef.current = null;
    };

    rec.onresult = (event: SpeechRecognitionEvent) => {
      if (recognitionRef.current !== rec) return; // sesión vieja: ignorar
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0]?.transcript ?? '';
        if (text.trim()) sessionHadSpeechRef.current = true;
        if (result.isFinal) finalsRef.current = composeTranscript(finalsRef.current, text);
        else interim = composeTranscript(interim, text);
      }
      interimRef.current = interim;
      setTranscript(composeTranscript(finalsRef.current, interim));
    };

    rec.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (recognitionRef.current !== rec) return;
      // Acá NO se toca finalsRef ni phase: entregar o descartar es cosa de onend.
      switch (classifySpeechError(event.error)) {
        case 'denied':
          wantsRecordingRef.current = false;
          pendingNoticeRef.current = 'denied';
          break;
        case 'no_mic':
          wantsRecordingRef.current = false;
          pendingNoticeRef.current = 'no_mic';
          break;
        case 'fatal':
          wantsRecordingRef.current = false;
          pendingNoticeRef.current = 'cut_off';
          break;
        case 'network':
          // Antes: cancelaba y borraba todo. Ahora: seguimos; onend reinicia.
          showReconnecting();
          pendingNoticeRef.current = 'cut_off'; // por si los reinicios no prosperan; onstart lo limpia si sí
          break;
        case 'silent':
          break; // no-speech / aborted ajeno / desconocido: onend decide
      }
    };

    rec.onend = () => {
      if (recognitionRef.current !== rec) return;
      recognitionRef.current = null;
      clearTimer(stopFallbackTimerRef);
      // NUNCA perder el interim: lo que la persona vio en la barra se conserva.
      finalsRef.current = composeTranscript(finalsRef.current, interimRef.current);
      interimRef.current = '';
      emptySessionsRef.current = sessionHadSpeechRef.current ? 0 : emptySessionsRef.current + 1;

      if (wantsRecordingRef.current) {
        if (emptySessionsRef.current < MAX_EMPTY_SESSIONS) {
          scheduleRestart(); // sigue en 'recording': barra y timer intactos
          return;
        }
        // Freno: N sesiones seguidas sin oír nada. Si el último error fue
        // network, el aviso ya es 'cut_off'; si no, fue silencio.
        wantsRecordingRef.current = false;
        pendingNoticeRef.current ??= 'silence';
      }
      deliverAndIdle();
    };

    try {
      rec.start();
    } catch {
      detach(rec);
      return false; // el caller decide reintentar
    }
    recognitionRef.current = rec;
    sessionHadSpeechRef.current = false;
    startAttemptsRef.current = 0;
    return true;
  };

  /** Reinicio con backoff: 200 → 400 → 800 ms según sesiones vacías / start() fallidos. */
  const scheduleRestart = () => {
    clearTimer(restartTimerRef);
    const delay = RESTART_DELAY_MS * 2 ** (emptySessionsRef.current + startAttemptsRef.current);
    restartTimerRef.current = setTimeout(() => {
      restartTimerRef.current = null;
      if (!wantsRecordingRef.current) return; // apretaron ✓/✕ en la ventana: ya lo resolvieron
      if (startSession()) return;
      if (++startAttemptsRef.current < MAX_START_ATTEMPTS) {
        scheduleRestart();
        return;
      }
      wantsRecordingRef.current = false;
      pendingNoticeRef.current = 'cut_off';
      deliverAndIdle();
    }, delay);
  };

  // Soporte + cleanup de unmount.
  useEffect(() => {
    if (!(window.SpeechRecognition || window.webkitSpeechRecognition)) setIsSupported(false);
    return () => {
      clearAllTimers();
      const rec = recognitionRef.current;
      recognitionRef.current = null;
      if (rec) {
        detach(rec);
        try { rec.abort(); } catch { /* ya cerrada */ }
      }
      // Política "lo que haya": si nos desmontan grabando entregamos en vez de
      // tirar. Sin setState: ya no estamos montados; solo refs, callbacks del
      // parent y el toast (que es global).
      //
      // En la variante panel esto saltea el paso de revisión del transcript
      // (el texto cae directo en el input del parent), así que se avisa. El
      // parent además deshabilita su toggle mientras se graba para que este
      // camino sea la excepción, no un gesto normal.
      if (wantsRecordingRef.current) {
        wantsRecordingRef.current = false;
        const text = composeTranscript(finalsRef.current, interimRef.current);
        finalsRef.current = '';
        interimRef.current = '';
        onRecordingChangeRef.current?.(false);
        if (text) {
          onTranscriptRef.current(text);
          if (variantRef.current === 'panel') {
            toast.message(tRef.current('performance.voice_input.moved_to_input'));
          }
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timer mm:ss mientras graba. Invariante: los reinicios internos no tocan
  // `phase`, por eso este effect no se re-ejecuta y `elapsed` no vuelve a 0.
  useEffect(() => {
    if (phase !== 'recording') return;
    setElapsed(0);
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const startRecording = () => {
    if (wantsRecordingRef.current) return; // doble click
    finalsRef.current = '';
    interimRef.current = '';
    emptySessionsRef.current = 0;
    startAttemptsRef.current = 0;
    pendingNoticeRef.current = null;
    setTranscript('');
    setHasTranscript(false);
    setReconnecting(false);
    wantsRecordingRef.current = true;
    if (!startSession()) {
      wantsRecordingRef.current = false;
      toast.message(tRef.current('performance.voice_input.error'));
      return;
    }
    clearTimer(maxDurationTimerRef);
    maxDurationTimerRef.current = setTimeout(() => {
      pendingNoticeRef.current = 'max_duration';
      finishRecording();
    }, MAX_TOTAL_MS);
    setPhase('recording'); // el effect del timer pone elapsed=0 acá y solo acá
    onRecordingChangeRef.current?.(true);
  };

  const finishRecording = () => {
    if (!wantsRecordingRef.current) return;
    wantsRecordingRef.current = false; // ANTES de stop(): onend entrega en vez de reiniciar
    clearTimer(restartTimerRef);
    const rec = recognitionRef.current;
    if (!rec) {
      deliverAndIdle(); // estábamos en la ventana entre onend y el reinicio
      return;
    }
    rec.stop(); // → onend → deliverAndIdle
    stopFallbackTimerRef.current = setTimeout(() => {
      if (recognitionRef.current === rec) deliverAndIdle();
    }, STOP_FALLBACK_MS);
  };

  const cancelRecording = () => {
    wantsRecordingRef.current = false;
    clearAllTimers();
    const rec = recognitionRef.current;
    recognitionRef.current = null;
    if (rec) {
      detach(rec); // sin handlers no llega onend ni 'aborted': la limpieza es toda nuestra
      try { rec.abort(); } catch { /* ya cerrada */ }
    }
    finalsRef.current = '';
    interimRef.current = '';
    pendingNoticeRef.current = null;
    setTranscript('');
    setReconnecting(false);
    setPhase('idle');
    onRecordingChangeRef.current?.(false);
  };

  const handleUseTranscript = useCallback(() => {
    if (transcript.trim()) {
      onTranscript(transcript.trim());
      setTranscript('');
      setHasTranscript(false);
    }
  }, [transcript, onTranscript]);

  const isRecording = phase === 'recording';
  const micTitle =
    !isSupported ? t('performance.voice_input.not_supported')
    : phase === 'denied' ? t('performance.voice_input.mic_denied')
    : t('performance.voice_input.record');

  const bar = (
    <RecordingBar
      elapsed={elapsed}
      transcript={transcript}
      placeholder={t('performance.voice_input.speak_now')}
      cancelLabel={t('performance.voice_input.cancel')}
      doneLabel={t('performance.voice_input.done')}
      onCancel={cancelRecording}
      onDone={finishRecording}
    />
  );

  // Va AFUERA de la barra, arriba, para no tocar su layout de una línea.
  const reconnectingLine = reconnecting && (
    <p className="px-1 text-[11px] leading-tight text-amber-600 dark:text-amber-400">
      {t('performance.voice_input.reconnecting')}
    </p>
  );

  if (variant === 'inline') {
    if (isRecording) {
      return (
        <div className={cn('flex flex-1 min-w-0 flex-col gap-1', className)}>
          {reconnectingLine}
          {bar}
        </div>
      );
    }
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={startRecording}
        disabled={!isSupported}
        className={cn(
          'rounded-full shrink-0',
          phase === 'denied' && 'text-red-500',
          className,
        )}
        title={micTitle}
      >
        <Mic className="h-4 w-4" />
      </Button>
    );
  }

  // ── variante panel (Mapa de Talento) ──────────────────────────────────────
  if (!isSupported) {
    return (
      <p className="text-xs text-muted-foreground px-1">
        {t('performance.voice_input.not_supported')}
      </p>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {isRecording ? (
        <div className="flex flex-col gap-1">
          {reconnectingLine}
          <div className="flex">{bar}</div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={startRecording}
            className={cn('gap-2 transition-colors', phase === 'denied' && 'border-red-300 text-red-600')}
            title={micTitle}
          >
            <Mic className="h-3.5 w-3.5" />
            {t('performance.voice_input.record')}
          </Button>
          {phase === 'denied' && (
            <span className="text-xs text-red-600">{t('performance.voice_input.mic_denied')}</span>
          )}
        </div>
      )}

      {(hasTranscript || (!isRecording && transcript)) && (
        <div className="space-y-1.5">
          <Textarea
            value={transcript}
            onChange={e => setTranscript(e.target.value)}
            placeholder={t('performance.voice_input.transcript_placeholder')}
            rows={2}
            className="text-sm resize-none"
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={handleUseTranscript}
            disabled={!transcript.trim()}
            className="gap-1.5 h-7 text-xs"
          >
            <Check className="h-3.5 w-3.5" />
            {t('performance.voice_input.use_transcript')}
          </Button>
        </div>
      )}
    </div>
  );
}
